// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

// Tema renkleri icin kontrast tabani. Hangi paleti secerse secsin yardimci metin
// okunakli kalsin diye tema uygulanirken tek yerden gecirilir.

interface Rgb {
  r: number;
  g: number;
  b: number;
  a: number;
}

function parseColor(value: string): Rgb | null {
  const v = value.trim();
  const hex = /^#([0-9a-f]{3,8})$/i.exec(v);
  if (hex) {
    const h = hex[1] ?? '';
    const expanded = h.length === 3 || h.length === 4
      ? [...h].map((c) => c + c).join('')
      : h;
    if (expanded.length !== 6 && expanded.length !== 8) return null;
    const n = (i: number): number => parseInt(expanded.slice(i, i + 2), 16);
    return { r: n(0), g: n(2), b: n(4), a: expanded.length === 8 ? n(6) / 255 : 1 };
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(v);
  if (rgb) {
    const parts = (rgb[1] ?? '').split(/[,/\s]+/).filter(Boolean).map(Number);
    const [r, g, b, a] = parts;
    if (r === undefined || g === undefined || b === undefined || parts.some((p) => Number.isNaN(p))) return null;
    return { r, g, b, a: a ?? 1 };
  }
  return null;
}

function toHex({ r, g, b }: Rgb): string {
  const c = (n: number): string => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

// Yari saydam rengi zemine yedir — kontrast hesabi opak renk ister.
function flatten(fg: Rgb, bg: Rgb): Rgb {
  if (fg.a >= 1) return fg;
  return {
    r: fg.r * fg.a + bg.r * (1 - fg.a),
    g: fg.g * fg.a + bg.g * (1 - fg.a),
    b: fg.b * fg.a + bg.b * (1 - fg.a),
    a: 1,
  };
}

function luminance({ r, g, b }: Rgb): number {
  const ch = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

// Renk koyu mu? Koyu tema paletinin gercekten koyu oldugunu dogrulamak icin.
// Tema Studyosu icerik rengini koyu palete AYNEN kopyalayabiliyor (2026-08-05 dev'de olculdu:
// colors.card = colorsDark.card = #e9fbf9) — o palet uygulanirsa acik zemin koyu-tema
// kurallariyla cakisir ve metin okunmaz olur.
export function isDarkColor(color: string): boolean {
  const rgb = parseColor(color);
  return rgb ? luminance(rgb) < 0.5 : false;
}

export function contrastRatio(foreground: string, background: string): number {
  const bg = parseColor(background);
  const raw = parseColor(foreground);
  if (!bg || !raw) return 21;
  const fg = flatten(raw, bg);
  const l1 = luminance(fg);
  const l2 = luminance(bg);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

function mixToward(color: Rgb, target: Rgb, ratio: number): Rgb {
  return {
    r: color.r + (target.r - color.r) * ratio,
    g: color.g + (target.g - color.g) * ratio,
    b: color.b + (target.b - color.b) * ratio,
    a: 1,
  };
}

/**
 * Rengi zemine gore en az istenen kontrasta ulasana kadar koyulastirir/acikllastirir.
 * Zaten yeterliyse renge dokunmaz.
 */
export function ensureContrast(foreground: string, background: string, minRatio = 4.5): string {
  const bg = parseColor(background);
  const raw = parseColor(foreground);
  if (!bg || !raw) return foreground;

  const flat = flatten(raw, bg);
  if (contrastRatio(toHex(flat), background) >= minRatio) {
    return raw.a >= 1 ? foreground : toHex(flat);
  }

  // Zemin acikta siyaha, koyuda beyaza dogru cek.
  const target: Rgb = luminance(bg) > 0.5 ? { r: 0, g: 0, b: 0, a: 1 } : { r: 255, g: 255, b: 255, a: 1 };
  let best = toHex(flat);
  for (let step = 1; step <= 20; step++) {
    const candidate = toHex(mixToward(flat, target, step / 20));
    best = candidate;
    if (contrastRatio(candidate, background) >= minRatio) break;
  }
  return best;
}
